import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/Bits";
import { ChatThread } from "@/components/ChatThread";
import { Inbox as InboxIcon, MessagesSquare } from "lucide-react";

function lastMessagePreview(t) {
  if (!t.last_message) return "No messages yet";
  if (t.last_message.kind === "notice") return `Notice: ${t.last_message.subject}`;
  return t.last_message.body;
}

export default function Inbox() {
  const [threads, setThreads] = useState(null);
  const [params, setParams] = useSearchParams();
  const selected = params.get("tenant") || "";
  const selectedRef = useRef(selected);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  const load = useCallback(() => api.get("/chat/threads").then((r) => {
    setThreads(r.data);
    if (!selectedRef.current && r.data.length) setParams({ tenant: r.data[0].tenant_id }, { replace: true });
  }), [setParams]);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  if (!threads) return <Loading />;

  return (
    <div>
      <PageHeader title="Inbox" subtitle="Direct conversations with your tenants. Notices you send also land here so replies stay in one place." />
      {threads.length === 0 ? (
        <EmptyState icon={InboxIcon} title="No tenants yet" subtitle="Add a tenant to start a conversation." testid="inbox-empty" />
      ) : (
        <div className="grid lg:grid-cols-[300px_1fr] gap-5 h-[calc(100vh-220px)] min-h-[520px]">
          <div className="rounded-2xl border border-slate-200 bg-white overflow-y-auto" data-testid="inbox-threads">
            {threads.map((t) => (
              <button key={t.tenant_id} onClick={() => setParams({ tenant: t.tenant_id })} data-testid={`thread-${t.tenant_id}`}
                className={`w-full text-left px-4 py-3.5 border-b border-slate-100 flex items-start gap-3 transition-colors ${selected === t.tenant_id ? "bg-emerald-50/70" : "hover:bg-slate-50"}`}>
                <div className="h-9 w-9 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xs font-bold shrink-0">{(t.name || "?").slice(0, 2).toUpperCase()}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-slate-900 truncate">{t.name}</span>
                    {t.unread > 0 && <span className="rounded-full bg-primary text-primary-foreground text-[10px] font-bold px-1.5 py-0.5 min-w-[18px] text-center" data-testid={`thread-unread-${t.tenant_id}`}>{t.unread}</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">{t.property_name || "No active lease"}</div>
                  <div className="text-xs text-slate-500 truncate mt-0.5">{lastMessagePreview(t)}</div>
                </div>
              </button>
            ))}
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden flex flex-col">
            {selected ? <ChatThread tenantId={selected} myRole="landlord" onRead={() => setThreads((ts) => ts?.map((t) => (t.tenant_id === selected ? { ...t, unread: 0 } : t)))} />
              : <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-2"><MessagesSquare className="h-8 w-8" /><p className="text-sm">Pick a tenant to start chatting</p></div>}
          </div>
        </div>
      )}
    </div>
  );
}
